import { useMemo } from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, isGroupRadius, isIndividualRadius,
} from '~/_shared/types/proximity/proximity.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { getProximityRadius } from '~/_shared/utils/proximity/getProximityRadius';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { getDriveTimePolygonShortLabel } from '~/proximity/proximity.helpers';
import { useProximities } from '~/proximity/useProximities';
import { useBoundaryStateItem } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import {
  useGetBoundaryFilterRequest, useGetBoundaryTerritoryFilterGroups,
} from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useMarkersInBoundary';
import type { BoundaryFilterBoundariesRequest } from '~/spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import type { BoundaryTerritoryFilterGroups } from '~/spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import type { RadiusFilterRequestArguments } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import type {
  BoundaryGroupId,
  BoundaryId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import type { AreaRequestGetterFilterParams } from '~/store/spreadsheetData/area/spreadsheetData.area.helpers';
import { useSpreadsheetPreviewFilterStateSelector } from './activeMapElements.selectors';

export type SpreadsheetPreviewFilter = Pick<
  AreaRequestGetterFilterParams,
    'circles' | 'multiPolygons' | 'boundaries' | 'boundaryTerritories'
> & {
  names: string[];
  isEmpty: boolean;
};

export const useSpreadsheetPreviewFilters = (): SpreadsheetPreviewFilter => {
  const [t] = useTranslation();
  const spreadsheetPreviewFilterState = useSpreadsheetPreviewFilterStateSelector();
  const proximities = useProximities();
  const latLngData = useLatLngSpreadsheetData();
  const getBoundaryTerritoriesFilter = useGetBoundaryTerritoryFilterGroups();
  const getBoundariesFilter = useGetBoundaryFilterRequest();
  const { getBoundaryTerritoryDisplayName } = useGetBoundaryTerritoryDisplayNames();
  const { getBoundaryStateItem } = useBoundaryStateItem();

  return useMemo(() => {
    const names: string[] = [];
    const circles: RadiusFilterRequestArguments[] = [];
    const multiPolygons: MultiPolygon[] = [];
    const boundaries: BoundaryFilterBoundariesRequest[] = [];
    const boundaryTerritories: BoundaryTerritoryFilterGroups[] = [];

    spreadsheetPreviewFilterState.proximities.forEach((filteredProximity) => {
      const proximityById = proximities.find((proximity) => proximity.id === filteredProximity.proximityId);
      if (!proximityById) {
        return;
      }

      if (isDriveTimePolygon(proximityById) && proximityById.data.paths) {
        names.push(t('Drive Time Polygon: {{time}}', {
          time: getDriveTimePolygonShortLabel(proximityById.data.hours, proximityById.data.minutes),
        }));
        multiPolygons.push(proximityById.data.paths);
      }

      if (isGroupRadius(proximityById) || isIndividualRadius(proximityById)) {
        const specificRowId = filteredProximity.spreadsheetRowId;

        let latLngIfGroupProximity: LatLng | undefined = undefined;
        if (isGroupRadius(proximityById) && specificRowId) { // we only support 1 circle, so if group, specificRowId needs to be set
          latLngIfGroupProximity = latLngData.getRow(specificRowId);
        }
        const latLng = isIndividualRadius(proximityById)
          ? proximityById.data.data
          : latLngIfGroupProximity;

        const radialDistance = getProximityRadius(proximityById, specificRowId);

        if (radialDistance && latLng) {
          names.push(t('Proximity Radius{{subtype}}: {{distance}}{{unit}}', {
            subtype: '',
            distance: radialDistance.radius,
            unit: t(radialDistance.unit),
          }));
          circles.push({
            center: latLng,
            ...radialDistance,
          });
        }
      }
    });

    const boundaryIdsByBoundaryGroup = new Map<BoundaryGroupId, { ids: BoundaryId[]; btgId: BoundaryTerritoryGroupId }>();
    spreadsheetPreviewFilterState.boundaries.forEach((boundary) => {
      const entry = boundaryIdsByBoundaryGroup.get(boundary.boundaryGroupId) ?? { ids: [], btgId: boundary.boundaryTerritoryGroupId };
      entry.ids.push(boundary.boundaryId);
      boundaryIdsByBoundaryGroup.set(boundary.boundaryGroupId, entry);
    });
    boundaryIdsByBoundaryGroup.forEach((boundaryIds, boundaryGroupId) => {
      const boundaryFilter = getBoundariesFilter(boundaryIds.btgId, boundaryIds.ids);
      if (boundaryFilter) {
        names.push(`${t('Boundary')} | ${boundaryIds.ids.map((boundaryId) =>
          getBoundaryStateItem(boundaryId, boundaryGroupId)?.displayName ?? t('Unknown')
        ).join(', ')}`);
        boundaries.push(boundaryFilter.boundaries);
      }
    });

    spreadsheetPreviewFilterState.boundaryTerritories.forEach((boundaryTerritory) => {
      const boundaryTerritoryFilter = getBoundaryTerritoriesFilter(
        boundaryTerritory.boundaryTerritoryGroupId, boundaryTerritory.boundaryTerritoryId
      );
      if (boundaryTerritoryFilter) {
        const btgName = getBoundaryTerritoryDisplayName(boundaryTerritory.boundaryTerritoryId, boundaryTerritory.boundaryTerritoryGroupId);
        names.push(`${t('Territory')} | ${btgName}`);
        boundaryTerritories.push(...boundaryTerritoryFilter);
      }
    });

    const isEmpty = names.length === 0;

    return {
      names,
      circles: circles.length > 0 ? circles : undefined,
      multiPolygons: multiPolygons.length > 0 ? multiPolygons : undefined,
      boundaries: boundaries[0] ? boundaries[0] : undefined, //for now we only support one BG
      boundaryTerritories: boundaryTerritories.length > 0 ? boundaryTerritories : undefined,
      isEmpty,
    };
  }, [getBoundariesFilter, getBoundaryStateItem, getBoundaryTerritoriesFilter, getBoundaryTerritoryDisplayName, latLngData, proximities, spreadsheetPreviewFilterState.boundaries, spreadsheetPreviewFilterState.boundaryTerritories, spreadsheetPreviewFilterState.proximities, t]);
};
